import type { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  selectImpressionsByTypeLiveRows,
  selectKpiCompareLiveReach,
  selectReportingV2AnyLoading,
  selectReportingV2Filters,
  selectReportingV2Widget,
  selectTrendLiveRows,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { CloseRounded, DescriptionOutlined, PictureAsPdfOutlined, TableChartOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  Typography,
} from '@mui/material';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { isConversionsColumnShown, isDspEntityScope, isPerPlatformScope, isTotalRowMismatched } from '../format';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import {
  EXPORT_SECTIONS,
  type ExportSection,
  PDF_ONLY_SECTIONS,
  appendImpressionsByTypeTotal,
  buildSpreadsheetXml,
  buildStackedCsv,
  downloadFile,
  excludedExportSections,
  presentRowsForExport,
} from '../lib/reportExport';
import { COLORS } from '../theme';
import { aggregateDaypartRows, buildDaypartExportRows } from './DaypartCard';
import { toExportRows } from './TopPublishersCard';

/**
 * daypartMetric's raw widget rows are unbucketed hour_of_day integers (flank#4838) —
 * the card only bucketed them locally for its own chart/per-widget export, so this
 * dialog used to export the raw 24 rows straight from the store. Infer which metric
 * is currently selected from the row's own keys (impressions is always present, so
 * ctr/vcr — never both — is what distinguishes a rate selection) and re-run the same
 * bucketing DaypartCard uses, rather than re-exporting the raw per-hour rows.
 */
export const toDaypartExportRows = (rows: ReportRow[]) => {
  // review, CodeRabbit: bucketing an empty result produces 6 zero-valued
  // dayparts — a failed or still-loading request would export as "zero
  // delivery everywhere" rather than staying empty. Pass empty straight
  // through, same as every other EXPORT_SECTIONS row set already does.
  if (rows.length === 0) return rows;
  const metric = rows.some(row => 'ctr' in row) ? 'ctr' : rows.some(row => 'vcr' in row) ? 'vcr' : 'impressions';
  const { labels, values } = aggregateDaypartRows(rows, metric);
  return buildDaypartExportRows(labels, values, metric);
};

type Format = 'csv' | 'excel' | 'pdf';

const FORMATS: Array<{ value: Format; label: string; hint: string; icon: ReactElement }> = [
  {
    value: 'csv',
    label: 'CSV',
    hint: 'One file, all sections stacked',
    icon: <TableChartOutlined sx={{ fontSize: 18 }} />,
  },
  {
    value: 'excel',
    label: 'Excel',
    hint: 'One sheet per section, sized columns',
    icon: <DescriptionOutlined sx={{ fontSize: 18 }} />,
  },
  {
    value: 'pdf',
    label: 'PDF',
    hint: 'Snapshot of the page as you see it',
    icon: <PictureAsPdfOutlined sx={{ fontSize: 18 }} />,
  },
];

/**
 * Export dialog, mirroring the FLANK 2.0 reference. Exports are built from the
 * widget rows already in the store, so a file always matches what's on screen —
 * same window, filters and grouping.
 */
const ExportDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [format, setFormat] = useState<Format>('csv');
  const [chooseSections, setChooseSections] = useState(false);
  const [selected, setSelected] = useState<string[]>([...EXPORT_SECTIONS, ...PDF_ONLY_SECTIONS].map(s => s.key));
  const filters = useAppSelector(selectReportingV2Filters);
  // A widget mid-refetch keeps its PREVIOUS rows in the store rather than
  // clearing them (avoids a flash of "0 rows" on every filter change) — but
  // that means exporting while a fetch is still in flight silently reads
  // stale, pre-change data with no indication anything is wrong (Earl, the
  // export appeared to ignore a just-applied filter/selection).
  const dataLoading = useAppSelector(selectReportingV2AnyLoading);
  // flank#4625 sweep — an export used to carry every raw row key regardless
  // of what the on-screen widgets hide; filter it through the same checks.
  const permissions = useReportingPermissions();

  // Read every section's rows up front: hooks can't be called conditionally, and
  // "current view" vs "chosen sections" is only a filter over the same set.
  //
  // totalRows is only threaded through for impressionsByType — the one
  // section that's genuinely server-paged (flank#4567: its widget only ever
  // holds the current page, so an export from it can silently be truncated
  // with nothing saying so). Every other section already holds its full
  // result set; leaving their totalRows unset guarantees this dialog can't
  // start disclosing something new for them off the back of this ticket.
  const allRows = useAppSelector((state: RootState) =>
    EXPORT_SECTIONS.map(s => {
      const widget = selectReportingV2Widget(s.widgetKey)(state);
      const publisherViewActive = s.key === 'impressionsByType' && state.reportingV2.publisherInventoryActive;
      // Live DV360 reach (flank#4479) replaces the summed reach on screen for three
      // widgets, each a different shape: impressionsByType and trend hold full row
      // arrays, totals holds one current-period snapshot — so totals merges the
      // live values into its existing row(s) rather than swapping the array like
      // the other two (CodeRabbit / preprod QA: the export silently fell back to
      // summed canonical reach for both totals and trend).
      const liveRows =
        s.key === 'impressionsByType'
          ? selectImpressionsByTypeLiveRows(state)
          : s.key === 'trend'
            ? selectTrendLiveRows(state)
            : null;
      const kpiCompareLive = s.key === 'totals' ? selectKpiCompareLiveReach(state) : null;
      return {
        ...s,
        liveReach: Boolean(liveRows) || Boolean(kpiCompareLive),
        rows: publisherViewActive
          ? []
          : liveRows
            ? liveRows
            : kpiCompareLive
              ? widget.rows.map(row => ({ ...row, reach: kpiCompareLive.reach, frequency: kpiCompareLive.frequency }))
              : s.key === 'topPublishersMetric'
                ? toExportRows(widget.rows)
                : s.key === 'daypartMetric'
                  ? toDaypartExportRows(widget.rows)
                  : widget.rows,
        totalRows: s.key === 'impressionsByType' && !publisherViewActive ? widget.totalRows : undefined,
        clientSpendRestricted: widget.restrictedFields.includes('metrics.client_spend'),
        publisherViewActive,
      };
    }),
  );
  // Impressions by Type's Total row (flank#4645) — the same ungrouped
  // aggregate KpiStrip reads, read here too so the export can append it
  // without re-summing the (paged) rows above itself.
  const impressionsByTypeTotals = useAppSelector(selectReportingV2Widget('totals'));

  const toggle = (key: string) =>
    setSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

  const handleExport = () => {
    if (format === 'pdf') {
      // The print stylesheet already produces the page snapshot; a separate PDF
      // renderer would be a second thing to keep in agreement with the page.
      // "Choose sections" narrows it the same way CSV/Excel do below — reusing
      // the print stylesheet's own rv2-noprint hide rule (index.css) rather than
      // a second, PDF-only notion of what a "section" is (flank#4578: PDF used
      // to ignore this entirely and always printed every section).
      const hidden = excludedExportSections(chooseSections, selected)
        .map(key => document.querySelector<HTMLElement>(`[data-export-section="${key}"]`))
        .filter((el): el is HTMLElement => el !== null);
      for (const el of hidden) el.classList.add('rv2-noprint');
      const restore = () => {
        for (const el of hidden) el.classList.remove('rv2-noprint');
        window.removeEventListener('afterprint', restore);
      };
      window.addEventListener('afterprint', restore);

      onClose();
      setTimeout(() => window.print(), 0);
      return;
    }

    // "Current view" means every section on screen — the scope switch narrows
    // which sections are included, not which columns.
    const sections: ExportSection[] = allRows
      .filter(s => (chooseSections ? selected.includes(s.key) : true))
      .map(({ key, label, rows, totalRows, clientSpendRestricted, publisherViewActive, liveReach }) => {
        const presented = presentRowsForExport(rows, key, filters.dsps, permissions, liveReach);
        // Detect the active breakdown from the row shape itself — this dialog
        // has no other view into which chip produced these rows.
        const dims = Object.keys(rows[0] ?? {});
        // Same reasoning as above: a row's own keys include
        // its breakdown dimension (e.g. a `dsp` key on a by-DSP row, an
        // `advertiser` key on an Advertiser row), so the active metric scope
        // can be read off `rows[0]` the same way, without this dialog needing
        // the grid's own tab/sub-tab state (review, CodeRabbit).
        const reachColumnActive = liveReach || isPerPlatformScope(filters.dsps, dims) || isDspEntityScope(dims);
        const conversionsColumnActive = isConversionsColumnShown(filters.dsps, dims);
        // app_name/domain/publisher read a different source table than the
        // ungrouped 'totals' aggregate (flank#4649 review) — still appended
        // per flank#4645's AC, but the file needs its own copy of the
        // explanation (review, Lucas, flank#4683): a hover tooltip doesn't
        // travel into a CSV/Excel file.
        const impressionsByTypeMismatch = key === 'impressionsByType' && isTotalRowMismatched(dims);
        // Require a resolved, error-free totals widget with a row before
        // appending (review, CodeRabbit): a failed/in-flight totals request
        // must not append a Total row with every additive value blank.
        const totalsReady =
          !impressionsByTypeTotals.loading &&
          !impressionsByTypeTotals.error &&
          impressionsByTypeTotals.rows[0] !== undefined;
        return {
          key,
          label,
          clientSpendRestricted,
          publisherViewActive,
          rows:
            key === 'impressionsByType' && totalsReady
              ? appendImpressionsByTypeTotal(presented, impressionsByTypeTotals.rows[0], totalRows ?? rows.length, {
                  reachColumn: reachColumnActive,
                  conversionsColumn: conversionsColumnActive,
                })
              : presented,
          totalRows,
          // Gate on `totalsReady` too (review, Lucas/CodeRabbit, flank#4683):
          // otherwise a failed totals request still flags a mismatch note for
          // a Total row that was never actually appended.
          totalRowMismatch: impressionsByTypeMismatch && totalsReady,
        };
      });

    const stamp = `${filters.dateStart}_to_${filters.dateEnd}`;
    if (format === 'csv') {
      const meta = [
        'Ribeye Unified Reporting',
        `Date range: ${filters.dateStart} to ${filters.dateEnd}`,
        `Exported: ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`,
      ];
      downloadFile(buildStackedCsv(sections, meta), `unified-reporting_${stamp}.csv`, 'text/csv');
    } else {
      downloadFile(buildSpreadsheetXml(sections), `unified-reporting_${stamp}.xls`, 'application/vnd.ms-excel');
    }
    onClose();
  };

  // review, CodeRabbit: CSV/Excel can't hold the PDF-only sections, so leaving
  // only those checked must still count as "nothing chosen" for those formats
  // — otherwise Export stays enabled and produces a file with no report data.
  const nothingChosen =
    chooseSections &&
    (format === 'pdf' ? selected.length === 0 : !EXPORT_SECTIONS.some(section => selected.includes(section.key)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pb: 0.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='h6'>Export report</Typography>
          <Typography variant='body2' sx={{ color: COLORS.muted, mt: 0.5 }}>
            {dataLoading
              ? "Data is still loading for your current filters — exporting now would include the previous selection's numbers."
              : "Exports match what's on screen — current date range, filters and grouping. Columns you can't see are never included."}
          </Typography>
        </Box>
        <IconButton size='small' onClick={onClose} aria-label='Close export dialog'>
          <CloseRounded fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant='caption' sx={{ color: COLORS.muted, fontWeight: 600, letterSpacing: 0.6 }}>
          FORMAT
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, mb: 2.5 }}>
          {FORMATS.map(f => (
            <Box
              key={f.value}
              role='radio'
              aria-checked={format === f.value}
              tabIndex={0}
              onClick={() => setFormat(f.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFormat(f.value);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.25,
                border: 1,
                borderRadius: 1.5,
                cursor: 'pointer',
                borderColor: format === f.value ? COLORS.primary : 'divider',
                bgcolor: format === f.value ? COLORS.pageBg : 'transparent',
              }}
            >
              <Radio checked={format === f.value} size='small' sx={{ p: 0 }} />
              {f.icon}
              <Box>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {f.label}
                </Typography>
                <Typography variant='caption' sx={{ color: COLORS.muted }}>
                  {f.hint}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography variant='caption' sx={{ color: COLORS.muted, fontWeight: 600, letterSpacing: 0.6 }}>
          SCOPE
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
          <FormControlLabel
            control={<Radio size='small' checked={!chooseSections} onChange={() => setChooseSections(false)} />}
            label={<Typography variant='body2'>Current view</Typography>}
          />
          <FormControlLabel
            control={<Radio size='small' checked={chooseSections} onChange={() => setChooseSections(true)} />}
            label={<Typography variant='body2'>Choose sections</Typography>}
          />
        </Box>

        {chooseSections && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {EXPORT_SECTIONS.map(s => (
              <FormControlLabel
                key={s.key}
                sx={{ display: 'flex' }}
                control={<Checkbox size='small' checked={selected.includes(s.key)} onChange={() => toggle(s.key)} />}
                label={<Typography variant='body2'>{s.label}</Typography>}
              />
            ))}
            {/* PDF-only (flank#4602) — no widgetKey/rows, so these only affect
                the print output and are hidden for CSV/Excel, which can't hold them. */}
            {format === 'pdf' &&
              PDF_ONLY_SECTIONS.map(s => (
                <FormControlLabel
                  key={s.key}
                  sx={{ display: 'flex' }}
                  control={<Checkbox size='small' checked={selected.includes(s.key)} onChange={() => toggle(s.key)} />}
                  label={<Typography variant='body2'>{s.label}</Typography>}
                />
              ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {nothingChosen && (
          <Typography variant='caption' sx={{ color: COLORS.red, textAlign: 'right' }}>
            Select at least one section to export.
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            disableElevation
            disabled={nothingChosen || dataLoading}
            onClick={handleExport}
            sx={{ textTransform: 'none', bgcolor: COLORS.black, '&:hover': { bgcolor: '#000' } }}
          >
            {dataLoading ? 'Loading…' : 'Export'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
